import React, { useMemo } from 'react';
import { Info } from 'lucide-react';
import { EDM_UI, mergeLintError } from '../../../../utils/edm';

export const CUSTOM_HTML_WARN_BYTES = 150 * 1024;
export const CUSTOM_HTML_MAX_BYTES = 200 * 1024;

let utf8: TextEncoder | null = null;

/** UTF-8 byte length (what the server measures), in one pass. */
export const htmlByteSize = (v: string): number => (utf8 ??= new TextEncoder()).encode(v).length;

/** Lint error for custom HTML, or null. Blocks Save, Preview and Freeze. */
export function customHtmlLint(v: string, bytes: number = htmlByteSize(v)): string | null {
  if (bytes > CUSTOM_HTML_MAX_BYTES) return 'Over 200 KB. Gmail clips long emails; shorten the HTML.';
  return mergeLintError(v);
}

const CustomHtmlPanel: React.FC<{
  value: string;
  onChange: (v: string) => void;
  disabled?: boolean;
  serverError?: string | null;
}> = ({ value, onChange, disabled, serverError }) => {
  const bytes = useMemo(() => htmlByteSize(value), [value]);
  const lint = useMemo(() => customHtmlLint(value, bytes), [value, bytes]);
  const kb = (bytes / 1024).toFixed(1);
  const over = bytes > CUSTOM_HTML_MAX_BYTES;
  const warn = !over && bytes > CUSTOM_HTML_WARN_BYTES;

  return (
    <div className="space-y-4">
      <div className={`${EDM_UI.infoBanner} flex gap-2`}>
        <Info className="mt-0.5 h-4 w-4 flex-shrink-0" />
        <div>
          <div className="font-semibold">Build your design with Claude</div>
          <p className="mt-1">
            Ask Claude (claude.ai) to design your email: describe the message, your brand colours and any images (use full
            https:// image links). Ask for &quot;a single-column HTML email, 600px wide, inline styles only, no scripts, no
            forms, no external stylesheets&quot;. Paste the HTML it gives you below. Put <code>{'{{firstName}}'}</code> where
            you want the reader&apos;s first name. Do not add your own unsubscribe link, address or ABN: VALUTTEN adds the
            compliant footer to every email.
          </p>
        </div>
      </div>
      <div className="rounded-lg border border-gray-700 bg-gray-900/40 p-3 text-sm text-gray-300">
        <div className="font-semibold text-gray-100">What we remove for safety</div>
        <p className="mt-1">
          Scripts, forms and form fields, iframes, embedded objects, event handlers (such as <code>onclick</code>),{' '}
          <code>javascript:</code> links, background images in styles (<code>url(...)</code>), and any link that is not
          http, https or mailto. Merge fields are only allowed as plain text. Do not put a merge field inside a link; VALUTTEN adds the unsubscribe link itself. The preview on the
          right shows exactly what recipients get.
        </p>
      </div>
      <div>
        <label htmlFor="edm-custom-html" className={EDM_UI.label}>
          Email HTML
        </label>
        <textarea
          id="edm-custom-html"
          rows={18}
          value={value}
          disabled={disabled}
          onChange={(e) => onChange(e.target.value)}
          className={`${EDM_UI.input} font-mono text-xs`}
          spellCheck={false}
        />
        <div className={`mt-1 text-xs ${over ? 'text-red-400' : warn ? 'text-amber-300' : 'text-gray-500'}`}>
          {kb} KB of 200 KB
        </div>
        {lint && <p className="mt-1 text-xs text-red-400">{lint}</p>}
      </div>
      {serverError && <div className={EDM_UI.errorBanner}>{serverError}</div>}
    </div>
  );
};

export default CustomHtmlPanel;
